import React from 'react'
import { MenuItem, Select, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ITableComponentInstance, TableSortDirection, DEFAULT_TABLE_STYLES } from 'src/types/report-designer'
import { AppTextField } from '../../AppTextField'
import { IFormSchemaColumn } from 'src/generated-types/form-schema'
import { ColorSwatchField, FontSizeSelect } from './PropertyComponents'
import {
  getTextFontColors,
  StyledDivider,
  SectionHeading,
  PropertiesWrapper,
  FieldSection,
  PropertyLabel,
} from './PropertiesShared.styles'
import { getBackgroundColors } from './PropertyConfigurationSection.action'
import { useTableComponentHandlers } from './TableComponentProperties.action'
import { PlaceholderText } from './TableComponentProperties.style'
import { ColumnSectionPanel } from './ColumnSectionPanel'

export interface ITableComponentPropertiesProps {
  instance: ITableComponentInstance
  onChange: (updated: ITableComponentInstance) => void
  columns?: IFormSchemaColumn[]
  isColumnsLoading: boolean
}

const TABLE_FONT_SIZES = [10, 11, 12, 13, 14, 15, 16, 17, 18, 19, 20]

export const TableComponentProperties: React.FC<ITableComponentPropertiesProps> = ({
  instance,
  onChange,
  columns,
  isColumnsLoading,
}) => {
  const theme = useTheme()
  const { t: translate } = useTranslation(['dynamic-report'])
  const FONT_COLORS = getTextFontColors(theme)
  const BACKGROUND_COLORS = getBackgroundColors(theme)

  const {
    dragOverColumnIndex,
    editingColumnId,
    draftColumn,
    handleAddColumn,
    handleEditColumn,
    handleSaveColumn,
    handleDraftFieldSelect,
    handleDraftWidthChange,
    handleTableStyleChange,
    handleSortColumnChange,
    handleSortDirectionChange,
    handleDeleteColumn,
    handleColumnDragStart,
    handleColumnDragOver,
    handleColumnDrop,
    handleColumnDragEnd,
    handleCancelEdit,
    handleRowHeightChange,
  } = useTableComponentHandlers({ instance, onChange, columns, theme })

  const selectedFieldIds = instance.columns.map(col => col.formSchemaFieldId).filter((id): id is string => id !== null)

  const availableFieldsForDraft = columns
    ? columns.filter(f => !selectedFieldIds.includes(f.identifier) || f.identifier === draftColumn?.formSchemaFieldId)
    : []

  const effectiveHeaderColor = instance.tableStyles?.headerColor ?? DEFAULT_TABLE_STYLES(theme).headerColor
  const effectiveHeaderBgColor =
    instance.tableStyles?.headerBackgroundColor ?? DEFAULT_TABLE_STYLES(theme).headerBackgroundColor
  const effectiveFontSize = instance.tableStyles?.fontSize ?? DEFAULT_TABLE_STYLES(theme).fontSize
  const effectiveRowHeight = instance.tableStyles?.rowHeight ?? DEFAULT_TABLE_STYLES(theme).rowHeight

  return (
    <PropertiesWrapper>
      <SectionHeading>{translate('table-styles', { ns: 'dynamic-report' })}</SectionHeading>
      <FieldSection>
        <PropertyLabel>{translate('header-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={FONT_COLORS}
          selectedColor={effectiveHeaderColor}
          onColorChange={color => handleTableStyleChange('headerColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('header-background-color', { ns: 'dynamic-report' })}</PropertyLabel>
        <ColorSwatchField
          colors={BACKGROUND_COLORS}
          selectedColor={effectiveHeaderBgColor}
          onColorChange={color => handleTableStyleChange('headerBackgroundColor', color)}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('table-font-size', { ns: 'dynamic-report' })}</PropertyLabel>
        <FontSizeSelect
          value={effectiveFontSize}
          onChange={size => handleTableStyleChange('fontSize', size)}
          sizes={TABLE_FONT_SIZES}
        />
      </FieldSection>

      <FieldSection>
        <PropertyLabel>{translate('row-height', { ns: 'dynamic-report' })}</PropertyLabel>
        <AppTextField
          size="small"
          fullWidth
          type="number"
          inputProps={{ min: 1 }}
          value={String(effectiveRowHeight)}
          onChange={handleRowHeightChange}
        />
      </FieldSection>

      <StyledDivider />

      <ColumnSectionPanel
        columns={instance.columns}
        isColumnsLoading={isColumnsLoading}
        editingColumnId={editingColumnId}
        draftColumn={draftColumn}
        dragOverColumnIndex={dragOverColumnIndex}
        availableFieldsForDraft={availableFieldsForDraft}
        schemaColumns={columns}
        onAddColumn={handleAddColumn}
        onEditColumn={handleEditColumn}
        onSaveColumn={handleSaveColumn}
        onCancelEdit={handleCancelEdit}
        onDeleteColumn={handleDeleteColumn}
        onDraftFieldSelect={handleDraftFieldSelect}
        onDraftWidthChange={handleDraftWidthChange}
        onColumnDragStart={handleColumnDragStart}
        onColumnDragOver={handleColumnDragOver}
        onColumnDrop={handleColumnDrop}
        onColumnDragEnd={handleColumnDragEnd}
      />
      {instance.columns.length > 0 && (
        <>
          <StyledDivider />
          <SectionHeading>{translate('table-sort', { ns: 'dynamic-report' })}</SectionHeading>
          <FieldSection>
            <PropertyLabel>{translate('sort-column', { ns: 'dynamic-report' })}</PropertyLabel>
            <Select
              size="small"
              fullWidth
              value={instance.sortColumnId ?? ''}
              onChange={handleSortColumnChange}
              displayEmpty
              MenuProps={{
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
              renderValue={selected => {
                if (!selected) {
                  return <PlaceholderText>{translate('no-sort-column', { ns: 'dynamic-report' })}</PlaceholderText>
                }
                const col = instance.columns.find(c => c.id === selected)
                return col?.fieldName || `#${col?.orderNumber ?? ''}`
              }}
              disabled={instance.columns.length === 0}
            >
              {instance.columns.map(col => (
                <MenuItem key={col.id} value={col.id}>
                  {col.fieldName || `#${col.orderNumber}`}
                </MenuItem>
              ))}
            </Select>
          </FieldSection>

          <FieldSection>
            <PropertyLabel>{translate('sort-direction', { ns: 'dynamic-report' })}</PropertyLabel>
            <Select
              size="small"
              fullWidth
              value={instance.sortDirection ?? TableSortDirection.None}
              onChange={handleSortDirectionChange}
              disabled={!instance.sortColumnId}
              MenuProps={{
                anchorOrigin: { vertical: 'top', horizontal: 'left' },
                transformOrigin: { vertical: 'bottom', horizontal: 'left' },
              }}
            >
              <MenuItem value={TableSortDirection.None}>{translate('sort-none', { ns: 'dynamic-report' })}</MenuItem>
              <MenuItem value={TableSortDirection.Asc}>{translate('sort-asc', { ns: 'dynamic-report' })}</MenuItem>
              <MenuItem value={TableSortDirection.Desc}>{translate('sort-desc', { ns: 'dynamic-report' })}</MenuItem>
            </Select>
          </FieldSection>
        </>
      )}
    </PropertiesWrapper>
  )
}
